import { supabase } from "@/integrations/supabase/client";

type EventType = 'page_view' | 'feature_use' | 'game_action' | 'session';

interface AnalyticsEvent {
  event_type: EventType;
  event_name: string;
  metadata?: Record<string, string | number | boolean>;
}

// Debounce map to prevent duplicate events
const recentEvents = new Map<string, number>();
const DEBOUNCE_MS = 1000;

function shouldTrack(eventKey: string): boolean {
  const now = Date.now();
  const lastTime = recentEvents.get(eventKey);
  
  if (lastTime && now - lastTime < DEBOUNCE_MS) {
    return false;
  }
  
  recentEvents.set(eventKey, now);
  
  // Clean up old entries periodically
  if (recentEvents.size > 100) {
    const cutoff = now - DEBOUNCE_MS * 2;
    for (const [key, time] of recentEvents.entries()) {
      if (time < cutoff) recentEvents.delete(key);
    }
  }
  
  return true;
}

async function getUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  } catch {
    return null;
  }
}

export async function trackServerEvent(
  eventType: EventType,
  eventName: string,
  metadata?: Record<string, string | number | boolean>
): Promise<void> {
  const eventKey = `${eventType}:${eventName}`;
  
  if (!shouldTrack(eventKey)) {
    return;
  }

  try {
    const userId = await getUserId();
    
    await supabase.from('analytics_events').insert({
      user_id: userId,
      event_type: eventType,
      event_name: eventName,
      metadata: metadata || {}
    });
  } catch (error) {
    // Silent fail - analytics shouldn't break the app
    console.debug('Analytics event failed:', error);
  }
}

export function trackPageView(pageName: string): void {
  trackServerEvent('page_view', pageName, {
    referrer: document.referrer || 'direct',
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight
  });
}

export function trackFeature(featureName: string, metadata?: Record<string, string | number | boolean>): void {
  trackServerEvent('feature_use', featureName, metadata);
}

export function trackGameAction(actionName: string, metadata?: Record<string, string | number | boolean>): void {
  trackServerEvent('game_action', actionName, metadata);
}

export function trackSessionStart(): void {
  trackServerEvent('session', 'start', {
    user_agent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform
  });
}

export function trackSessionEnd(): void {
  trackServerEvent('session', 'end');
}

// Initialize session tracking
export function initServerAnalytics(): void {
  trackSessionStart();
  
  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    // Use sendBeacon for reliable tracking on page close
    const userId = localStorage.getItem('wormble-last-user-id'); // cached user id
    
    navigator.sendBeacon?.(
      `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`,
      JSON.stringify({
        user_id: userId,
        event_type: 'session',
        event_name: 'end',
        metadata: {}
      })
    );
  });
  
  // Track visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackServerEvent('session', 'background');
    } else {
      trackServerEvent('session', 'foreground');
    }
  });
}
